import {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {eachMonthOfInterval, format, parse, parseISO} from "date-fns";
import {Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import RefreshIcon from "@mui/icons-material/Refresh";
import {fetchPlans, plansSelectors} from "../store/plansSlice";
import {featureFlagLabel} from "../constants/featureFlagLabels";
import {PageHeader} from "../components/ui/PageHeader";
import {SectionCard} from "../components/ui/SectionCard";
import {chartPreviousPeriod, chartRamp, chartTrack, chipTones, colors} from "../theme/tokens";
import {planTone} from "../utils/planTone";
import {formatCount, formatDateTime} from "../util/format";
import {fetchAnalytics} from "../store/analyticsSlice";
import {fetchTenants, tenantsSelectors} from "../store/tenantsSlice";
import {useAppDispatch, useAppSelector} from "../store/hooks";
import type {TenantPublicDetails, TenantSummaryResponse} from "../types/tenant";
import {getSubscriptionDisplayEndDate, hasActiveSubscriptionAccess} from "../utils/subscriptionDates";

function useDashboardData() {
    const {t, i18n} = useTranslation();
    const dispatch = useAppDispatch();
    const tenants = useAppSelector(tenantsSelectors.selectAll);
    const tenantsStatus = useAppSelector((s) => s.tenants.status);
    const analyticsData = useAppSelector((s) => s.analytics.data);
    const analyticsStatus = useAppSelector((s) => s.analytics.status);

    useEffect(() => {
        if (tenantsStatus === "idle") void dispatch(fetchTenants());
    }, [dispatch, tenantsStatus]);

    useEffect(() => {
        if (analyticsStatus === "idle") void dispatch(fetchAnalytics());
    }, [dispatch, analyticsStatus]);

    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 60_000);
        return () => clearInterval(id);
    }, []);

    const activeTenants = useMemo(() => {
        return tenants.filter((t) => t.activeSubscription);
    }, [tenants]);

    const inactiveTenants = useMemo(() => {
        return tenants.filter((t) => {
            if (!t.activeSubscription) return true;
            return !hasActiveSubscriptionAccess(t.activeSubscription, now);
        });
    }, [tenants, now]);

    const payingTenants = useMemo(
            () => activeTenants.filter((t) => (t.activeSubscription?.amountDueInCents ?? 0) > 0),
            [activeTenants],
    );

    const freeTenants = useMemo(
            () => activeTenants.filter((t) => (t.activeSubscription?.amountDueInCents ?? 0) <= 0),
            [activeTenants],
    );

    const activeEmployeesCount = useMemo(() => {
        return activeTenants.reduce((sum, t) => sum + (t.activeSubscription?.maxEmployees ?? 0), 0);
    }, [activeTenants]);

    const totalAppointmentCount = analyticsData?.totalAppointmentCount ?? 0;
    const totalCustomerCount = analyticsData?.totalCustomerCount ?? 0;
    const tenantPublicDetails = analyticsData?.tenantsPublicDetails ?? [];

    const signupTimelineData = useMemo(() => {
        if (!activeTenants.length) return [];
        const startDate = new Date(2023, 0, 1);
        const endDate = now;
        const months = eachMonthOfInterval({start: startDate, end: endDate});
        const byMonth = new Map<string, number>();
        months.forEach((m) => byMonth.set(format(m, "yyyy-MM"), 0));
        activeTenants.forEach((t) => {
            const key = format(parseISO(t.createdAt), "yyyy-MM");
            if (byMonth.has(key)) byMonth.set(key, (byMonth.get(key) ?? 0) + 1);
        });
        return Array.from(byMonth.entries())
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([key, count]) => ({
                    name: format(parse(key, "yyyy-MM", new Date()), "MMM yy"),
                    count,
                }));
    }, [activeTenants, now]);

    const tenantsBySubscriptionData = useMemo(() => {
        if (!activeTenants.length) return [];
        const map = new Map<string, number>();
        activeTenants.forEach((tenant) => {
            const planKey = tenant.activeSubscription?.planId ?? "__NO_PLAN__";
            map.set(planKey, (map.get(planKey) ?? 0) + 1);
        });
        return Array.from(map.entries()).map(([key, count]) => ({
            name: key === "__NO_PLAN__" ? t("NO_PLAN") : key,
            planId: key === "__NO_PLAN__" ? null : key,
            count,
        }));
    }, [activeTenants, t]);

    const subscriptionsTimelineData = useMemo(() => {
        if (!activeTenants.length) return [];
        const byMonth = new Map<string, number>();
        activeTenants.forEach((t) => {
            if (!t.activeSubscription) return;
            const endDate = getSubscriptionDisplayEndDate(t.activeSubscription);
            const key = format(endDate, "yyyy-MM");
            byMonth.set(key, (byMonth.get(key) ?? 0) + 1);
        });
        return Array.from(byMonth.entries())
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([key, count]) => ({
                    name: format(parse(key, "yyyy-MM", new Date()), "MMM yy"),
                    count,
                }));
    }, [activeTenants]);

    const featureKeys = [
        "smsSending",
        "onlineBooking",
        "fileUpload",
        "themePicker",
        "precalculatedAvailabilities",
        "firstLaunch",
        "shiftSchedule",
    ] as const;

    const tenantsByFeatureData = useMemo(() => {
        if (!activeTenants.length) return [];
        const map = new Map<string, number>(featureKeys.map((k) => [k, 0]));
        activeTenants.forEach((tenant) => {
            const fc = tenant.featureConfig;
            if (!fc) return;
            featureKeys.forEach((k) => {
                if (fc[k]) map.set(k, (map.get(k) ?? 0) + 1);
            });
        });
        return Array.from(map.entries()).map(([key, count]) => ({
            name: featureFlagLabel(t, key),
            count,
        }));
    }, [activeTenants, t]);

    const filterPublicDetailsByActive = (
            details: TenantPublicDetails[],
            active: TenantSummaryResponse[],
    ): TenantPublicDetails[] => {
        if (!active.length) return details;
        const ids = new Set(active.map((t) => t.id));
        return details.filter((d) => ids.has(d.id));
    };

    const publicDetailsForCharts = useMemo(
            () => filterPublicDetailsByActive(tenantPublicDetails, activeTenants),
            [tenantPublicDetails, activeTenants],
    );

    const unknownLabel = t("UNKNOWN");
    const tenantsByCountryData = useMemo(() => {
        if (!publicDetailsForCharts.length) return [];
        const map = new Map<string, number>();
        publicDetailsForCharts.forEach((d) => {
            let label = unknownLabel;
            if (d.countryAlpha2) {
                try {
                    label = new Intl.DisplayNames([i18n.language], {type: "region"}).of(d.countryAlpha2) ?? d.countryAlpha2;
                } catch {
                    label = d.countryAlpha2;
                }
            }
            map.set(label, (map.get(label) ?? 0) + 1);
        });
        return Array.from(map.entries())
                .sort((a, b) => b[1] - a[1])
                .map(([name, count]) => ({name, count}));
    }, [publicDetailsForCharts, unknownLabel, i18n.language]);

    const tenantsByLanguageData = useMemo(() => {
        if (!publicDetailsForCharts.length) return [];
        const map = new Map<string, number>();
        const sentinel = "__UNKNOWN__";
        publicDetailsForCharts.forEach((d) => {
            const raw = d.notificationsLanguage ?? sentinel;
            let label: string;
            if (raw === sentinel) {
                label = unknownLabel;
            } else {
                try {
                    label = new Intl.DisplayNames([i18n.language], {type: "language"}).of(raw) ?? raw;
                } catch {
                    label = raw;
                }
            }
            map.set(label, (map.get(label) ?? 0) + 1);
        });
        return Array.from(map.entries())
                .sort((a, b) => b[1] - a[1])
                .map(([name, count]) => ({name, count}));
    }, [publicDetailsForCharts, unknownLabel, i18n.language]);

    const tenantsByCurrencyData = useMemo(() => {
        if (!publicDetailsForCharts.length) return [];
        const map = new Map<string, number>();
        publicDetailsForCharts.forEach((d) => {
            const c = d.currency ?? unknownLabel;
            map.set(c, (map.get(c) ?? 0) + 1);
        });
        return Array.from(map.entries())
                .sort((a, b) => b[1] - a[1])
                .map(([name, count]) => ({name, count}));
    }, [publicDetailsForCharts, unknownLabel]);

    const tenantsByTimezoneData = useMemo(() => {
        if (!publicDetailsForCharts.length) return [];
        const map = new Map<string, number>();
        publicDetailsForCharts.forEach((d) => {
            const z = d.ianaTimezoneName ?? unknownLabel;
            map.set(z, (map.get(z) ?? 0) + 1);
        });
        return Array.from(map.entries())
                .sort((a, b) => b[1] - a[1])
                .map(([name, count]) => ({name, count}));
    }, [publicDetailsForCharts, unknownLabel]);

    const minShort = t("MINUTES_SHORT");
    const tenantsByAvailabilityIntervalData = useMemo(() => {
        if (!publicDetailsForCharts.length) return [];
        const map = new Map<string, number>();
        publicDetailsForCharts.forEach((d) => {
            const interval =
                    d.availabilityIntervalInMinutes != null
                            ? `${d.availabilityIntervalInMinutes} ${minShort}`
                            : unknownLabel;
            map.set(interval, (map.get(interval) ?? 0) + 1);
        });
        return Array.from(map.entries())
                .sort((a, b) => b[1] - a[1])
                .map(([name, count]) => ({name, count}));
    }, [publicDetailsForCharts, unknownLabel, minShort]);

    const daysLabel = t("DAYS");
    const hoursLabel = t("HOURS");
    const tenantsByBookingAdvanceData = useMemo(() => {
        if (!publicDetailsForCharts.length) return [];
        const map = new Map<string, number>();
        publicDetailsForCharts.forEach((d) => {
            let label = unknownLabel;
            const adv = d.bookingAllowedInAdvance;
            if (adv) {
                if (adv.days != null) label = `${adv.days} ${daysLabel}`;
                else if (adv.hours != null) label = `${adv.hours} ${hoursLabel}`;
            }
            map.set(label, (map.get(label) ?? 0) + 1);
        });
        return Array.from(map.entries())
                .sort((a, b) => b[1] - a[1])
                .map(([name, count]) => ({name, count}));
    }, [publicDetailsForCharts, unknownLabel, daysLabel, hoursLabel]);

    const requiredLabel = t("REQUIRED");
    const notRequiredLabel = t("NOT_REQUIRED");
    const tenantsByEmailConfirmationData = useMemo(() => {
        if (!publicDetailsForCharts.length) return [];
        const map = new Map<string, number>();
        publicDetailsForCharts.forEach((d) => {
            const label = d.customerEmailConfirmationRequired ? requiredLabel : notRequiredLabel;
            map.set(label, (map.get(label) ?? 0) + 1);
        });
        return Array.from(map.entries()).map(([name, count]) => ({name, count}));
    }, [publicDetailsForCharts, requiredLabel, notRequiredLabel]);

    const refresh = () => {
        void dispatch(fetchTenants());
        void dispatch(fetchAnalytics());
    };

    return {
        refresh,
        loading: tenantsStatus === "loading" || analyticsStatus === "loading",
        tenantsError: useAppSelector((s) => s.tenants.error),
        analyticsError: useAppSelector((s) => s.analytics.error),
        activeTenants,
        inactiveTenants,
        payingTenants,
        freeTenants,
        activeEmployeesCount,
        totalAppointmentCount,
        totalCustomerCount,
        signupTimelineData,
        tenantsBySubscriptionData,
        subscriptionsTimelineData,
        tenantsByFeatureData,
        tenantsByCountryData,
        tenantsByLanguageData,
        tenantsByCurrencyData,
        tenantsByTimezoneData,
        tenantsByAvailabilityIntervalData,
        tenantsByBookingAdvanceData,
        tenantsByEmailConfirmationData,
    };
}

type Datum = {name: string; count: number};

const SIGNUP_WINDOW_MONTHS = 24;
const SHARE_BAR_CATEGORIES = 3;

function StatTile({label, value}: {label: string; value: number}) {
    return (
            <Box sx={{px: 2, py: 1.25, minWidth: 0}}>
                <Typography title={label} sx={{fontSize: "12px", color: colors.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{label}</Typography>
                <Typography sx={{fontSize: "19px", fontWeight: 500, color: colors.ink, fontVariantNumeric: "tabular-nums"}}>{formatCount(value)}</Typography>
            </Box>
    );
}

function PayingTenantsCard({paying, active}: {paying: number; active: number}) {
    const {t} = useTranslation();
    const share = active > 0 ? paying / active : 0;
    return (
            <Paper variant="outlined" sx={{p: 2, display: "flex", flexDirection: "column", justifyContent: "center"}}>
                <Typography sx={{fontSize: "12.5px", color: colors.muted}}>{t("PAYING_TENANTS")}</Typography>
                <Typography sx={{fontSize: "26px", fontWeight: 500, color: colors.ink, fontVariantNumeric: "tabular-nums", lineHeight: 1.3}}>
                    {formatCount(paying)}
                </Typography>
                <Box sx={{height: 6, borderRadius: 3, backgroundColor: chartTrack, overflow: "hidden", my: 1}}>
                    <Box sx={{height: "100%", width: `${share * 100}%`, backgroundColor: colors.success}} />
                </Box>
                <Typography sx={{fontSize: "12px", color: colors.success}}>
                    {t("DASHBOARD_PAYING_SHARE", {percent: Math.round(share * 100), active: formatCount(active)})}
                </Typography>
            </Paper>
    );
}

function NewTenantsCard({data}: {data: Datum[]}) {
    const {t} = useTranslation();
    const months = data.slice(-SIGNUP_WINDOW_MONTHS);
    const split = Math.max(0, months.length - 12);
    const last12 = months.slice(split).reduce((sum, d) => sum + d.count, 0);
    const previous12 = months.slice(0, split).reduce((sum, d) => sum + d.count, 0);
    const change = previous12 > 0 ? Math.round(((last12 - previous12) / previous12) * 100) : null;
    return (
            <SectionCard
                    title={t("DASHBOARD_NEW_TENANTS_BY_MONTH")}
                    helper={
                        change === null
                                ? t("DASHBOARD_NEW_TENANTS_LAST_12", {count: last12})
                                : t("DASHBOARD_NEW_TENANTS_LAST_12_CHANGE", {count: last12, change: `${change >= 0 ? "+" : ""}${change}`})
                    }
                    actions={
                        <Box sx={{display: "flex", gap: 2, fontSize: "12px", color: colors.muted}}>
                            <LegendSwatch color={chartPreviousPeriod} label={t("DASHBOARD_PREVIOUS_12_MONTHS")} />
                            <LegendSwatch color={chartRamp[1]} label={t("DASHBOARD_LAST_12_MONTHS")} />
                        </Box>
                    }
            >
                {months.length === 0 ? (
                        <EmptyChart />
                ) : (
                        <ResponsiveContainer width="100%" height={240}>
                            <BarChart data={months} margin={{top: 8, right: 4, left: -16, bottom: 0}}>
                                <CartesianGrid vertical={false} stroke={chartTrack} />
                                <XAxis dataKey="name" tick={{fontSize: 11, fill: colors.muted}} tickLine={false} axisLine={false} interval={2} />
                                <YAxis tick={{fontSize: 11, fill: colors.faint}} tickLine={false} axisLine={false} allowDecimals={false} />
                                <Tooltip cursor={{fill: colors.rowHover}} />
                                <Bar dataKey="count" name={t("DASHBOARD_TENANTS")} radius={[2, 2, 0, 0]} isAnimationActive={false}>
                                    {months.map((entry, index) => (
                                            <Cell key={entry.name} fill={index < split ? chartPreviousPeriod : chartRamp[1]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                )}
            </SectionCard>
    );
}

function SubscriptionEndCard({data, now}: {data: Datum[]; now: Date}) {
    const {t} = useTranslation();
    const currentMonth = format(now, "MMM yy");
    const endingThisMonth = data.find((d) => d.name === currentMonth)?.count ?? 0;
    return (
            <SectionCard
                    title={t("DASHBOARD_SUBSCRIPTION_END_BY_MONTH")}
                    helper={<Box component="span" sx={{color: endingThisMonth > 0 ? colors.expiring : undefined}}>{t("DASHBOARD_ENDING_THIS_MONTH", {count: endingThisMonth})}</Box>}
            >
                {data.length === 0 ? (
                        <EmptyChart />
                ) : (
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={data} margin={{top: 16, right: 4, left: -16, bottom: 0}}>
                                <XAxis dataKey="name" tick={{fontSize: 11, fill: colors.muted}} tickLine={false} axisLine={false} />
                                <YAxis hide allowDecimals={false} />
                                <Tooltip cursor={{fill: colors.rowHover}} />
                                <Bar dataKey="count" name={t("DASHBOARD_TENANTS")} radius={[2, 2, 0, 0]} isAnimationActive={false} label={{position: "top", fontSize: 11, fill: colors.body}}>
                                    {data.map((entry) => (
                                            <Cell key={entry.name} fill={entry.name === currentMonth ? colors.expiring : chartRamp[1]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                )}
            </SectionCard>
    );
}

function HorizontalBars({rows, total, showPercent}: {rows: {label: string; count: number; color: string}[]; total: number; showPercent?: boolean}) {
    if (rows.length === 0) return <EmptyChart />;
    const max = Math.max(...rows.map((row) => row.count), 1);
    return (
            <Box component="dl" sx={{m: 0, display: "grid", gridTemplateColumns: "minmax(110px, auto) 1fr 48px", columnGap: 1.5, rowGap: 1, alignItems: "center"}}>
                {rows.map((row) => (
                        <Box key={row.label} sx={{display: "contents"}}>
                            <Box component="dt" sx={{fontSize: "12.5px", color: colors.body, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{row.label}</Box>
                            <Box aria-hidden sx={{height: 10, borderRadius: "2px", backgroundColor: chartTrack, overflow: "hidden"}}>
                                <Box sx={{height: "100%", width: `${((showPercent ? row.count / Math.max(total, 1) : row.count / max) * 100).toFixed(1)}%`, backgroundColor: row.color}} />
                            </Box>
                            <Box component="dd" sx={{m: 0, fontSize: "12.5px", textAlign: "right", color: colors.ink, fontVariantNumeric: "tabular-nums"}}>
                                {showPercent ? `${Math.round((row.count / Math.max(total, 1)) * 100)} %` : formatCount(row.count)}
                            </Box>
                        </Box>
                ))}
            </Box>
    );
}

function ShareBar({title, data}: {title: string; data: Datum[]}) {
    const {t} = useTranslation();
    const total = data.reduce((sum, d) => sum + d.count, 0);
    const top = data.slice(0, SHARE_BAR_CATEGORIES);
    const rest = data.slice(SHARE_BAR_CATEGORIES).reduce((sum, d) => sum + d.count, 0);
    const segments = rest > 0 ? [...top, {name: t("DASHBOARD_OTHER"), count: rest}] : top;
    const percent = (count: number) => `${Math.round((count / Math.max(total, 1)) * 100)}%`;
    return (
            <Box>
                <Typography sx={{fontSize: "12.5px", fontWeight: 500, color: colors.ink, mb: 0.75}}>{title}</Typography>
                {total === 0 ? (
                        <EmptyChart />
                ) : (
                        <>
                            <Box aria-hidden sx={{display: "flex", height: 10, borderRadius: "2px", overflow: "hidden", gap: "2px", mb: 1}}>
                                {segments.map((segment, index) => (
                                        <Box key={segment.name} sx={{flex: segment.count, backgroundColor: chartRamp[index] ?? chartRamp[chartRamp.length - 1]}} />
                                ))}
                            </Box>
                            <Box component="dl" sx={{m: 0, display: "grid", gridTemplateColumns: "1fr auto", rowGap: 0.25, fontSize: "12px"}}>
                                {segments.map((segment, index) => (
                                        <Box key={segment.name} sx={{display: "contents"}}>
                                            <Box component="dt" sx={{color: colors.body, display: "flex", alignItems: "center", gap: 0.75}}>
                                                <Box aria-hidden sx={{width: 8, height: 8, borderRadius: "1px", backgroundColor: chartRamp[index] ?? chartRamp[chartRamp.length - 1]}} />
                                                {segment.name}
                                            </Box>
                                            <Box component="dd" sx={{m: 0, textAlign: "right", color: colors.ink, fontVariantNumeric: "tabular-nums"}}>{percent(segment.count)}</Box>
                                        </Box>
                                ))}
                            </Box>
                        </>
                )}
            </Box>
    );
}

function LegendSwatch({color, label}: {color: string; label: string}) {
    return (
            <Box component="span" sx={{display: "inline-flex", alignItems: "center", gap: 0.75}}>
                <Box component="span" aria-hidden sx={{width: 10, height: 10, borderRadius: "2px", backgroundColor: color}} />
                {label}
            </Box>
    );
}

function EmptyChart() {
    const {t} = useTranslation();
    return <Typography variant="body2" sx={{color: colors.faint, py: 2}}>{t("DASHBOARD_NO_DATA")}</Typography>;
}

export function DashboardPage() {
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const plans = useAppSelector(plansSelectors.selectAll);
    const plansStatus = useAppSelector((s) => s.plans.status);
    const data = useDashboardData();
    const [loadedAt, setLoadedAt] = useState<Date | null>(null);

    useEffect(() => {
        if (plansStatus === "idle") void dispatch(fetchPlans());
    }, [dispatch, plansStatus]);

    useEffect(() => {
        if (!data.loading) setLoadedAt(new Date());
    }, [data.loading]);

    const now = new Date();
    const planName = (planId: string | null, fallback: string) => (planId ? plans.find((plan) => plan.id === planId)?.name ?? planId : fallback);
    const planRows = [...data.tenantsBySubscriptionData]
            .sort((a, b) => b.count - a.count)
            .map((row) => ({
                label: planName(row.planId, row.name),
                count: row.count,
                color: row.planId ? chipTones[planTone(row.planId, plans)].color : colors.placeholder,
            }));
    const featureRows = [...data.tenantsByFeatureData]
            .sort((a, b) => b.count - a.count)
            .map((row) => ({label: row.name, count: row.count, color: chartRamp[1]}));

    const counters: {label: string; value: number}[] = [
        {label: t("ACTIVE_TENANTS"), value: data.activeTenants.length},
        {label: t("INACTIVE_TENANTS"), value: data.inactiveTenants.length},
        {label: t("PAYING_TENANTS"), value: data.payingTenants.length},
        {label: t("FREE_TENANTS"), value: data.freeTenants.length},
        {label: t("DASHBOARD_MAX_EMPLOYEES"), value: data.activeEmployeesCount},
        {label: t("TOTAL_CUSTOMERS"), value: data.totalCustomerCount},
        {label: t("TOTAL_APPOINTMENTS"), value: data.totalAppointmentCount},
    ];

    const configurationCharts: {title: string; data: Datum[]}[] = [
        {title: t("DASHBOARD_TENANTS_BY_COUNTRY"), data: data.tenantsByCountryData},
        {title: t("DASHBOARD_TENANTS_BY_LANGUAGE"), data: data.tenantsByLanguageData},
        {title: t("DASHBOARD_TENANTS_BY_CURRENCY"), data: data.tenantsByCurrencyData},
        {title: t("DASHBOARD_TENANTS_BY_TIMEZONE"), data: data.tenantsByTimezoneData},
        {title: t("DASHBOARD_TENANTS_BY_AVAILABILITY_INTERVAL"), data: data.tenantsByAvailabilityIntervalData},
        {title: t("DASHBOARD_TENANTS_BY_BOOKING_ADVANCE"), data: data.tenantsByBookingAdvanceData},
        {title: t("DASHBOARD_TENANTS_BY_EMAIL_CONFIRMATION"), data: data.tenantsByEmailConfirmationData},
    ];

    return (
            <>
                <PageHeader
                        title={t("NAV_DASHBOARD")}
                        subtitle={
                            loadedAt
                                    ? t("DASHBOARD_SUBTITLE", {count: data.activeTenants.length, time: formatDateTime(loadedAt.toISOString())})
                                    : t("DASHBOARD_LOADING")
                        }
                        actions={
                            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={data.refresh} disabled={data.loading}>
                                {t("DASHBOARD_REFRESH")}
                            </Button>
                        }
                />

                {(data.tenantsError || data.analyticsError) && (
                        <Alert severity="error">
                            {data.tenantsError && <div>{t("DASHBOARD_ERROR_TENANTS")}: {data.tenantsError}</div>}
                            {data.analyticsError && <div>{t("DASHBOARD_ERROR_ANALYTICS")}: {data.analyticsError}</div>}
                        </Alert>
                )}

                {data.loading ? (
                        <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", md: "repeat(3, 1fr)"}, gap: "14px"}}>
                            {Array.from({length: 6}, (_, index) => (
                                    <Skeleton key={index} variant="rounded" height={index < 3 ? 96 : 260} />
                            ))}
                        </Box>
                ) : (
                        <>
                            <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", lg: "280px minmax(0, 1fr)"}, gap: "14px"}}>
                                <PayingTenantsCard paying={data.payingTenants.length} active={data.activeTenants.length} />
                                <Paper
                                        variant="outlined"
                                        component="section"
                                        aria-label={t("DASHBOARD_COUNTERS_ARIA")}
                                        sx={{display: "grid", gridTemplateColumns: {xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)", lg: "repeat(7, 1fr)"}, "& > *:not(:last-child)": {borderRight: `1px solid ${colors.sectionDivider}`}, alignContent: "center"}}
                                >
                                    {counters.map((counter) => (
                                            <StatTile key={counter.label} label={counter.label} value={counter.value} />
                                    ))}
                                </Paper>
                            </Box>

                            <NewTenantsCard data={data.signupTimelineData} />

                            <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", lg: "1.4fr 1fr 1fr"}, gap: "14px"}}>
                                <SubscriptionEndCard data={data.subscriptionsTimelineData} now={now} />
                                <SectionCard title={t("DASHBOARD_TENANTS_BY_PLAN")} helper={t("DASHBOARD_ACTIVE_SUBSCRIPTIONS")}>
                                    <HorizontalBars rows={planRows} total={data.activeTenants.length} />
                                </SectionCard>
                                <SectionCard title={t("DASHBOARD_OPTIONAL_FEATURES_IN_USE")} helper={t("DASHBOARD_FEATURES_HELPER")}>
                                    <HorizontalBars rows={featureRows} total={data.activeTenants.length} showPercent />
                                </SectionCard>
                            </Box>

                            <SectionCard title={t("DASHBOARD_TENANT_CONFIGURATION")} helper={t("DASHBOARD_TENANT_CONFIGURATION_HELPER")}>
                                <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)"}, columnGap: 4, rowGap: 3}}>
                                    {configurationCharts.map((chart) => (
                                            <ShareBar key={chart.title} title={chart.title} data={chart.data} />
                                    ))}
                                </Box>
                            </SectionCard>
                        </>
                )}
            </>
    );
}
